import { useLayoutEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useConfig } from "../lib/config";
import { choose, useCookieChoice } from "../lib/telemetry";
import { Button } from "./Button";
import "./cookiebox.css";

/** The custom property every layout reads to give the box its room.
 *
 *  Written on the root element rather than declared in CSS because the box's
 *  height is its sentence's height, which is the viewport's width's business:
 *  three lines at 320 and two at 520. A number typed into a stylesheet would
 *  be right at one width. */
const ROOM = "--cookiebox-h";

/**
 * The cookie box: one sentence, two buttons of the same size, and a link.
 *
 * **Accept and Reject are the same control twice.** Same tone, same height,
 * same width, same font weight, side by side and in that order only because
 * something has to be first. A box whose Reject is a grey line of text under
 * the button is a box that has counted a tap as a choice.
 *
 * **It is not shown at all where there is nothing to consent to.** No PostHog
 * project on the host means no box, because a box asking permission for
 * something that is not happening trains a parent to tap past the ones that
 * matter.
 *
 * It sits above the tab bar rather than over it, so the app is still usable
 * with the box open: a parent who wants to read the privacy notice first can
 * get there, and nothing in the product is held hostage to an answer.
 *
 * **And the page now gives it the room it takes.** Until the final audit of 5
 * September 2026 it did not: the box is fixed to the bottom of the viewport
 * and every layout was drawn as though it were not, so on `/capture` it sat
 * squarely over "Take a photo" (FP1-04) and on `/account` it ate the tap on
 * "Delete everything" (FP2-04). `document.elementFromPoint` at the centre of
 * both buttons returned the panel, and Playwright could not press either.
 *
 * The fix is measurement rather than a guess. The box publishes the space it
 * owns at the bottom of the viewport, from its own top edge downwards, as
 * `--cookiebox-h` on the root element. `styles/base.css` adds it to the page's
 * bottom padding, so the last control on a scrolling screen can always be
 * scrolled clear of it, and `styles/components.css` takes it off `.flow`'s
 * viewport height, so the capture and reading screens, which pin their action
 * to the bottom of the viewport, pin it above the box instead of under it.
 * The property goes when the box does, so a parent who has answered pays none
 * of it.
 *
 * Agent V5-C, wave 2. The room, Agent FIX-A1.
 */
export function CookieBox() {
  const cfg = useConfig();
  const made = useCookieChoice();
  const panel = useRef<HTMLDivElement>(null);
  const shown = !!cfg?.posthog && made === null;

  /* Layout rather than effect: the property is read by the same paint that
     shows the box, and an ordinary effect leaves one frame in which the box is
     drawn over a page that has not made room for it. */
  useLayoutEffect(() => {
    const root = document.documentElement;
    const clear = () => root.style.removeProperty(ROOM);
    const held = panel.current;
    if (!shown || !held) {
      clear();
      return;
    }
    /* From the panel's top edge to the bottom of the viewport, not the panel's
       height. The box floats above the tab bar, and the gap under it is space
       nothing else may use either. */
    const measure = () => {
      const room = Math.ceil(window.innerHeight - held.getBoundingClientRect().top);
      root.style.setProperty(ROOM, `${Math.max(0, room)}px`);
    };
    measure();
    /* The panel, and the page under it.
     *
     * The panel alone was enough while every screen was in the first bundle.
     * Since the route split of 7 September 2026 the box is drawn before the
     * screen it sits on: it measures itself at the very bottom of an empty
     * page, the screen then arrives with a tab bar and pushes the box up by
     * 65px, and the panel's own size never changed, so nothing fired and the
     * page went on reserving the smaller number. That is the FP1-04 and
     * FP2-04 defect again, a control under the box, so the observer watches
     * the document as well: a screen appearing changes the body's box, and a
     * re-measure costs one read of a rectangle. */
    const watching = new ResizeObserver(measure);
    watching.observe(held);
    watching.observe(document.body);
    window.addEventListener("resize", measure);
    return () => {
      watching.disconnect();
      window.removeEventListener("resize", measure);
      clear();
    };
  }, [shown]);

  if (!shown) return null;

  return (
    <div className="cookiebox" role="region" aria-label="Cookies">
      <div className="cookiebox-panel" ref={panel}>
        <p className="cookiebox-say">
          We count how this app is used, and we set a cookie for it only if you
          accept. <Link to="/privacy">Read the privacy notice</Link>
        </p>
        <div className="cookiebox-actions">
          <Button tone="secondary" onClick={() => choose("accept", cfg)}>
            Accept
          </Button>
          <Button tone="secondary" onClick={() => choose("reject", cfg)}>
            Reject
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * The same choice, revisited from the privacy notice.
 *
 * A consent that cannot be withdrawn on the screen that explains it is not a
 * consent. This renders what the parent chose, in a sentence, and one control
 * that changes it. Rejecting here wipes what PostHog wrote on this device as
 * well as stopping it, which `choose` does.
 *
 * Where the host has no PostHog project there is nothing to revisit and the
 * block says so, because a control that sets a preference about something
 * that is not running is worse than no control.
 */
export function CookieChoice() {
  const cfg = useConfig();
  const made = useCookieChoice();

  if (!cfg?.posthog) {
    return (
      <p>
        Nothing on this phone is counting how you use the app, so there is
        nothing to accept or refuse here.
      </p>
    );
  }

  return (
    <div className="cookiechoice">
      <p>
        {made === "accept"
          ? "You accepted counting with a cookie. You can change that here."
          : made === "reject"
            ? "You refused the cookie. We count nothing that is kept on this phone, and you can change that here."
            : "You have not answered yet. The box at the bottom of the screen is asking."}
      </p>
      <div className="cookiebox-actions">
        <Button
          tone="secondary"
          disabled={made === "accept"}
          onClick={() => choose("accept", cfg)}
        >
          Accept
        </Button>
        <Button
          tone="secondary"
          disabled={made === "reject"}
          onClick={() => choose("reject", cfg)}
        >
          Reject
        </Button>
      </div>
    </div>
  );
}
